import type { LandingComponents } from "@/puck/blocks/blockTypes";
import { createStudioImageBlockConfig } from "@/puck/blocks/shared/studioImageBlock";
import Logo from "@/components/Logo";
import { RawHtmlSnippet } from "@/components/common/RawHtmlSnippet";
import { BookDemoButton } from "@/puck/BookDemoButton";
import { studioLinkField } from "@/puck/fields/studioLinkField";
import {
  BuilderBookDemoBlock,
  BuilderFaqBlock,
  BuilderPartnerTickerBlock,
  BuilderTextBlock,
  BuilderWistiaVideoBlock,
} from "@/puck/clientBlocks";
import {
  ICLOSED_COLLECTIONS_EVENT_URL,
  ICLOSED_EVENT_TITLE,
} from "@/utils/iclosed";
import { renderStudioEditableText } from "@/utils/blogMdxSource";
import clsx from "clsx";

export const mediaConfigs: Pick<
  LandingComponents,
  | "FAQ"
  | "RichText"
  | "WistiaVideo"
  | "PartnerTicker"
  | "BookDemo"
  | "Snippet"
  | "Image"
  | "Footer"
> = {
  FAQ: {
    label: "FAQ",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "text", contentEditable: true },
      accent: { type: "text", contentEditable: true },
      items: {
        type: "array",
        min: 1,
        max: 8,
        getItemSummary: (item) => item.question,
        defaultItemProps: {
          question: "Question",
          answer: "Answer",
        },
        arrayFields: {
          question: { type: "text", contentEditable: true },
          answer: { type: "textarea", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "FAQ",
      title: "Questions",
      accent: "answered",
      items: [
        {
          question: "How do translations work?",
          answer:
            "Author the English layout, then translate all text fields into the supported locales.",
        },
      ],
    },
    render: BuilderFaqBlock,
  },
  RichText: {
    label: "Rich text",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      body: { type: "textarea", contentEditable: true },
      align: {
        type: "radio",
        options: [
          { label: "Left", value: "left" },
          { label: "Center", value: "center" },
        ],
      },
    },
    defaultProps: {
      eyebrow: "Why Gail",
      title: "Explain the page narrative in plain language",
      body:
        "Use this block for supporting copy, campaign context, or a compact proof section. Line breaks are preserved.",
      align: "left",
    },
    render: ({ eyebrow, title, body, align }) => (
      <section className="w-full bg-white px-4 py-16 md:py-24">
        <BuilderTextBlock
          className={clsx(
            "mx-auto max-w-3xl",
            align === "center" && "text-center",
          )}
        >
          {eyebrow ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="mt-4 text-3xl font-bold leading-tight text-black md:text-5xl">
            {title}
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-black/60 md:text-lg">
            {typeof body === "string"
              ? body.split("\n").map((paragraph, index) => (
                  <p key={`${paragraph}-${index}`}>{paragraph}</p>
                ))
              : renderStudioEditableText(body)}
          </div>
        </BuilderTextBlock>
      </section>
    ),
  },
  WistiaVideo: {
    label: "Wistia video / VSL",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      mediaId: {
        type: "text",
        label: "Wistia media ID (from the video's share/embed URL)",
      },
      autoplay: { type: "radio", options: [
        { label: "Off", value: false },
        { label: "On muted", value: true },
      ] },
      align: {
        type: "radio",
        options: [
          { label: "Left", value: "left" },
          { label: "Center", value: "center" },
        ],
      },
      background: {
        type: "select",
        options: [
          { label: "Surface", value: "surface" },
          { label: "White", value: "white" },
          { label: "Primary", value: "primary" },
        ],
      },
    },
    defaultProps: {
      eyebrow: "Watch the VSL",
      title: "See Gail in action",
      subtitle:
        "Drop in a Wistia media ID to add a video sales letter, product walkthrough, or campaign-specific demo.",
      mediaId: "",
      autoplay: false,
      align: "center",
      background: "surface",
    },
    render: ({ puck, ...props }) => (
      <BuilderWistiaVideoBlock {...props} isEditing={puck.isEditing} />
    ),
  },
  PartnerTicker: {
    label: "Partner ticker",
    fields: {
      label: { type: "text", contentEditable: true },
      compact: {
        type: "radio",
        label: "Spacing",
        options: [
          { label: "Compact", value: true },
          { label: "Standard", value: false },
        ],
      },
      showDivider: {
        type: "radio",
        label: "Dividers",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
    },
    defaultProps: {
      label: "Trusted by leading teams",
      compact: true,
      showDivider: true,
    },
    render: BuilderPartnerTickerBlock,
  },
  BookDemo: {
    label: "Book demo / iClosed",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "text", contentEditable: true },
      accent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      iclosedEventUrl: { type: "text", label: "iClosed event URL" },
      iclosedEventTitle: { type: "text", label: "iClosed event title" },
      benefits: {
        type: "array",
        min: 0,
        max: 5,
        getItemSummary: (item) => item.text,
        defaultItemProps: { text: "Benefit" },
        arrayFields: {
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Book a demo",
      title: "See Gail in action",
      accent: "with your use case",
      subtitle: "Pick a time and our team will tailor the walkthrough to your workflow.",
      iclosedEventUrl: ICLOSED_COLLECTIONS_EVENT_URL,
      iclosedEventTitle: ICLOSED_EVENT_TITLE,
      benefits: [
        { text: "30-minute walkthrough" },
        { text: "Use-case mapping" },
        { text: "No commitment" },
      ],
    },
    render: BuilderBookDemoBlock,
  },
  Snippet: {
    label: "HTML snippet",
    fields: {
      html: {
        type: "textarea",
        label: "Raw HTML (embed code, iframes, etc.)",
      },
      caption: { type: "text", label: "Caption (optional)", contentEditable: true },
    },
    defaultProps: {
      html: "",
      caption: "",
    },
    // No bg-white / section chrome — Snippet is often nested in Columns and
    // the embed HTML owns its own background. BlogSnippet uses the same shape.
    render: ({ html, caption }) => (
      <RawHtmlSnippet html={html} caption={caption} className="w-full" />
    ),
  },
  Image: createStudioImageBlockConfig("Image"),
  Footer: {
    label: "Landing footer",
    fields: {
      headline: { type: "textarea", contentEditable: true },
      ctaLabel: { type: "text" },
      ctaHref: studioLinkField("CTA link"),
      copyright: { type: "text" },
    },
    defaultProps: {
      headline: "Ready to build your next Gail campaign?",
      ctaLabel: "Book a demo",
      ctaHref: "#book-demo",
      copyright: "Gail",
    },
    render: ({ headline, ctaLabel, ctaHref, copyright }) => (
      <footer className="w-full bg-black px-4 py-16 text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <div>
            <Logo className="h-6 w-auto brightness-0 invert" />
            <h2 className="mt-8 max-w-2xl font-monument text-3xl font-bold leading-tight md:text-5xl">
              {headline}
            </h2>
          </div>
          <div className="flex flex-col items-start gap-4 md:items-end">
            <BookDemoButton href={ctaHref} className="bg-white text-black hover:bg-white/90">
              {ctaLabel}
            </BookDemoButton>
            <p className="font-monument text-xs text-white/70">
              (c) {new Date().getFullYear()} {copyright}
            </p>
          </div>
        </div>
      </footer>
    ),
  },
};
